var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * VA-27781 — the browser half of ending a dead session: a single `fetch`
 * interceptor, installed once.
 *
 * There are 56 fetch call sites and no shared response handler, so hooking each
 * one would be 56 chances to miss one — and the one that gets missed is the one
 * that strands a user. Wrapping `fetch` once covers every existing call, every
 * module that builds its own requests, and everything added later.
 *
 * It is deliberately narrow about what counts as an expired session: see
 * `shouldEndSessionOn401`. A 401 only ends the session when the request carried
 * our own bearer token.
 */
import { buildLoginRedirect, clearAccessTokenOnly, clearSessionStorage, shouldEndSessionOn401, } from './auth-session';
import { refreshNow, scheduleTokenRefresh } from './auth-refresh';
import { readAccessToken, readRefreshToken } from './auth-token-store';
/** Many requests can 401 at once; the session may only end once. */
let ending = false;
/** Pull the Authorization header out of either fetch call shape. */
const readAuthHeader = (input, init) => {
    const fromInit = init === null || init === void 0 ? void 0 : init.headers;
    if (fromInit) {
        if (fromInit instanceof Headers)
            return fromInit.get('Authorization') || '';
        if (Array.isArray(fromInit)) {
            const hit = fromInit.find(([k]) => k.toLowerCase() === 'authorization');
            return hit ? hit[1] : '';
        }
        const rec = fromInit;
        const key = Object.keys(rec).find((k) => k.toLowerCase() === 'authorization');
        if (key)
            return rec[key] || '';
    }
    if (typeof Request !== 'undefined' && input instanceof Request) {
        return input.headers.get('Authorization') || '';
    }
    return '';
};
const readUrl = (input) => {
    if (typeof input === 'string')
        return input;
    if (typeof Request !== 'undefined' && input instanceof Request)
        return input.url;
    return String(input || '');
};
/**
 * End the session: drop the dead token and send them to login, remembering where
 * they were so they land back there rather than on the dashboard.
 *
 * A hard location assignment rather than react-router: this runs from
 * non-component code (the fetch wrapper), where no navigate() is in scope, and a
 * full reload is the honest thing to do when every piece of app state behind the
 * page was built on a session that no longer exists.
 */
export const endExpiredSession = () => {
    if (ending)
        return;
    ending = true;
    clearSessionStorage();
    try {
        window.dispatchEvent(new Event('authChange'));
    }
    catch (_a) {
        // non-browser environment
    }
    const { pathname, search, hash } = window.location;
    const target = buildLoginRedirect(pathname, search, hash);
    if (`${pathname}${search}` !== target)
        window.location.assign(target);
};
/** Reset the one-shot latch. Tests only. */
export const __resetSessionEndLatch = () => {
    ending = false;
};
const withAuthToken = (input, init, token) => {
    const bearer = `Bearer ${token}`;
    if (typeof Request !== 'undefined' && input instanceof Request && !(init === null || init === void 0 ? void 0 : init.headers)) {
        const headers = new Headers(input.headers);
        headers.set('Authorization', bearer);
        return { input: new Request(input, { headers }) };
    }
    const headers = new Headers((init === null || init === void 0 ? void 0 : init.headers) || undefined);
    headers.set('Authorization', bearer);
    return { input, init: Object.assign(Object.assign({}, (init || {})), { headers }) };
};
const retryWithRefreshedToken = (original, input, init, sentToken) => __awaiter(void 0, void 0, void 0, function* () {
    if (!readRefreshToken())
        return null;
    const body = init === null || init === void 0 ? void 0 : init.body;
    if (typeof ReadableStream !== 'undefined' && body instanceof ReadableStream)
        return null;
    if (typeof Request !== 'undefined' && input instanceof Request && input.bodyUsed)
        return null;
    const sent = sentToken.replace(/^bearer\s+/i, '').trim();
    const current = readAccessToken();
    if (current && current !== sent) {
        const next = withAuthToken(input, init, current);
        const replayed = yield original(next.input, next.init);
        if (replayed.status !== 401) {
            scheduleTokenRefresh();
            return replayed;
        }
        return null;
    }
    const fresh = yield refreshNow();
    if (!fresh) {
        clearAccessTokenOnly();
        return null;
    }
    const next = withAuthToken(input, init, fresh);
    const replayed = yield original(next.input, next.init);
    if (replayed.status === 401)
        return null;
    scheduleTokenRefresh();
    return replayed;
});
let installed = false;
/**
 * Wrap `window.fetch` so any 401 on an authenticated request ends the session.
 * Idempotent — safe to call from a component that may mount more than once.
 */
export const installExpiredSessionRedirect = () => {
    if (installed || typeof window === 'undefined' || !window.fetch)
        return;
    installed = true;
    const original = window.fetch.bind(window);
    window.fetch = (input, init) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield original(input, init);
        try {
            const sentToken = readAuthHeader(input, init);
            if (shouldEndSessionOn401({
                status: response.status,
                url: readUrl(input),
                hadBearer: /^bearer\s+\S/i.test(sentToken),
            })) {
                const retried = yield retryWithRefreshedToken(original, input, init, sentToken);
                if (retried)
                    return retried;
                endExpiredSession();
            }
        }
        catch (_a) {
            // never let session bookkeeping break a real response
        }
        return response;
    });
};
